import type { DocumentRecord } from '@/types/catalog'
import type { ConnectorRecord } from '@/types/connector'
import { cleanDocumentName } from './utils/stripGraphPrefix'

export const GRID_LAYOUT = {
  COLUMNS: 2,
  MARGIN: 40,
  LEFT_OFFSET: 80,
} as const

export const DOCS_PER_PAGE = 4 // Documents rendered per canvas page (2×2 grid)

/** Numeric-aware, case- and accent-insensitive: "file2" < "File10", "a" == "A". */
const filenameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
const textCollator = new Intl.Collator(undefined, { sensitivity: 'base' })

const DIGIT_RUN = /(\d+)/
const NON_ASCII_DIGIT = /[^\P{Nd}0-9]/gu
const IS_DIGIT = /\p{Nd}/u

/**
 * Map a non-ASCII decimal digit ("７", "٢") to its ASCII form. Unicode lays out
 * each script's digits as contiguous 0-9 blocks, so the value is the offset
 * from the start of the run of digit code points.
 */
function toAsciiDigit(ch: string): string {
  const cp = ch.codePointAt(0) ?? 0
  let start = cp
  while (IS_DIGIT.test(String.fromCodePoint(start - 1))) start--
  return String((cp - start) % 10)
}

/**
 * Filename order: A to Z, case-insensitive, numeric-aware ("file2" < "File10").
 * A number with a leading zero is a code, not a quantity, so it compares digit
 * by digit: "0123 Pump" < "7 Valve" and "07" < "7". The collator's numeric mode
 * alone reads "0123" as 123 and puts it after "7".
 */
function compareFilenames(rawA: string, rawB: string): number {
  // The collator's numeric mode reads every script's digits, so normalise them
  // to ASCII first; otherwise "７" is a number to the collator but text to
  // DIGIT_RUN, and the two rules can disagree into a cycle.
  const a = rawA.replace(NON_ASCII_DIGIT, toAsciiDigit)
  const b = rawB.replace(NON_ASCII_DIGIT, toAsciiDigit)
  // split with a capture group alternates text, digits, text, …; odd indexes are digit runs.
  const aParts = a.split(DIGIT_RUN)
  const bParts = b.split(DIGIT_RUN)
  const n = Math.min(aParts.length, bParts.length)
  for (let i = 0; i < n; i++) {
    const x = aParts[i]
    const y = bParts[i]
    if (i % 2 === 0) {
      if (textCollator.compare(x, y) !== 0) break
    } else if (x !== y) {
      if (x[0] === '0' || y[0] === '0') return x < y ? -1 : 1
      break
    }
  }
  return filenameCollator.compare(a, b)
}

type SortKey = { name: string; uploadedAt: string | null }

/**
 * Resolve the sort key for a board item: the filename shown on its card plus
 * its upload timestamp (tiebreaker). Graph rows (P&ID/PFD) take the source
 * file's name, matching the card title, and fall back to their own name. The
 * store's copy of the source wins over the inline `pnid_source`, because a
 * rename updates the store row and leaves the inline copy stale.
 * Returns null when no record is found, which sorts to the end so unresolved
 * items don't shuffle with real ones.
 */
function getSortKey(
  id: string,
  docsById: Map<string, DocumentRecord>,
  connectorsById: Map<string, ConnectorRecord>,
): SortKey | null {
  const doc = docsById.get(id)
  if (doc) {
    let name = doc.name
    let type: string = doc.type
    if (doc.type === 'graph') {
      const source =
        (doc.properties?.pnid ? docsById.get(doc.properties.pnid) : undefined) ?? doc.pnid_source
      if (source) {
        name = source.name
        type = source.type
      }
    }
    return {
      name: cleanDocumentName(name ?? '', type),
      uploadedAt: doc.upload_started_at ?? doc.created_at ?? null,
    }
  }
  const connector = connectorsById.get(id)
  if (connector) return { name: connector.name ?? '', uploadedAt: connector.created_at ?? null }
  return null
}

/**
 * Sort board items by filename A to Z (case-insensitive, numeric-aware), then
 * by upload time, then by id so the order is deterministic. Items with no
 * resolved record sink to the end.
 *
 * `renamedNames` is the board's `renamedDocNames` overlay, keyed by card id.
 * The card title shows an override first, so the sort uses it too; otherwise
 * a just-renamed card would keep its old slot until documents reload.
 *
 * BoardCanvas (page rendering), BoardFileSearch (match navigation) and
 * boardSlice (blob preload) must all use the same order so prefetch and
 * search match what the user sees.
 */
export function sortByFilename<T extends { document_id?: string; id?: string }>(
  items: T[],
  documents: DocumentRecord[],
  connectors: ConnectorRecord[],
  renamedNames: Record<string, string> = {},
): T[] {
  const docsById = new Map(documents.map((d) => [d.id, d]))
  const connectorsById = new Map(connectors.map((c) => [c.id, c]))
  const keyed = items.map((item) => {
    const id = item.document_id ?? item.id ?? ''
    const key = getSortKey(id, docsById, connectorsById)
    const renamed = renamedNames[id]
    return { item, id, key: key && renamed !== undefined ? { ...key, name: renamed } : key }
  })
  keyed.sort((a, b) => {
    if (a.key && b.key) {
      const byName = compareFilenames(a.key.name, b.key.name)
      if (byName !== 0) return byName
      const aTs = a.key.uploadedAt
      const bTs = b.key.uploadedAt
      if (aTs && bTs) {
        if (aTs !== bTs) return aTs.localeCompare(bTs)
      } else if (aTs) {
        return -1
      } else if (bTs) {
        return 1
      }
    } else if (a.key) {
      return -1
    } else if (b.key) {
      return 1
    }
    return a.id.localeCompare(b.id)
  })
  return keyed.map((k) => k.item)
}

/**
 * Resolve the blob document ID to preload for a board document.
 * Cross-checks the actual document record type to handle legacy data
 * where graph documents were saved with doc_type 'pnid'/'pfd' (ENG-2289).
 *
 * Returns the document ID to download, or null if no blob preload is needed.
 */
export function resolveBlobDocId(
  doc: { document_id?: string; id?: string; doc_type?: string; type?: string },
  documents: DocumentRecord[],
): string | null {
  const docId = doc.document_id ?? doc.id
  if (!docId) return null
  const storedType = doc.doc_type || doc.type
  const fullDoc = documents.find((d) => d.id === docId)
  const actualType = fullDoc?.type ?? storedType
  if (actualType === 'graph') {
    return fullDoc?.properties?.pnid ?? null
  }
  if (actualType === 'document' || actualType === 'pnid' || actualType === 'pfd') {
    return docId
  }
  return null
}
